import { useState, useEffect, useRef } from "react";
import { Search, ChevronDown, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Option {
  id: number | string;
  name: string;
  [key: string]: any;
}

interface SearchableSelectProps {
  placeholder: string;
  searchFunction: (query: string) => Promise<any>;
  onSelect: (value: Option | null) => void;
  value?: Option | null;
  formatOption?: (option: Option) => string;
  disabled?: boolean;
  allowClear?: boolean;
  showColor?: boolean;
}

export function SearchableSelect({
  placeholder,
  searchFunction,
  onSelect,
  value,
  formatOption = (option) => option.name,
  disabled = false,
  allowClear = true,
  showColor = false,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setOptions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await searchFunction(searchTerm);
        if (data?.response?.data) {
          setOptions(data.response.data);
        } else {
          setOptions([]);
        }
      } catch (error) {
        console.error("Search error:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchFunction]);

  const handleSelect = (option: Option) => {
    onSelect(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = () => {
    onSelect(null);
    setSearchTerm("");
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        className={cn(
          "flex items-center w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "cursor-pointer focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled && "cursor-not-allowed opacity-50"
        )}
        onClick={handleInputClick}
      >
        {value ? (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2 truncate">
              {showColor && value.color && (
                <div
                  className="w-3 h-3 rounded-full border flex-shrink-0"
                  style={{ backgroundColor: value.color }}
                />
              )}
              <span className="truncate">{formatOption(value)}</span>
            </div>
            <div className="flex items-center gap-1">
              {allowClear && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-transparent"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between w-full">
            <span className="text-muted-foreground">{placeholder}</span>
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </div>
        )}
      </div>

      {isOpen && (
        <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-hidden">
          <CardContent className="p-2">
            <div className="relative mb-2">
              <Input
                placeholder={`Buscar ${placeholder.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
                autoFocus
              />
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>

            <div className="max-h-40 overflow-y-auto">
              {loading ? (
                <div className="px-2 py-3 text-sm text-gray-500 text-center">
                  Buscando...
                </div>
              ) : options.length > 0 ? (
                options.map((option) => (
                  <div
                    key={option.id}
                    className="px-2 py-2 text-sm hover:bg-gray-100 cursor-pointer rounded flex items-center gap-2"
                    onClick={() => handleSelect(option)}
                  >
                    {showColor && option.color && (
                      <div
                        className="w-3 h-3 rounded-full border"
                        style={{ backgroundColor: option.color }}
                      />
                    )}
                    <span>{formatOption(option)}</span>
                  </div>
                ))
              ) : searchTerm.length >= 2 ? (
                <div className="px-2 py-3 text-sm text-gray-500 text-center">
                  Nenhum resultado encontrado
                </div>
              ) : (
                <div className="px-2 py-3 text-sm text-gray-500 text-center">
                  Digite pelo menos 2 caracteres para buscar
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}